import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useApiPost } from '@/api/main/query'
import { CryptoWithdrawModel, NewCryptoWithdrawModel } from '@/entities/cryptoWithdraw'
import { cryptoWithdrawsAPI } from '@/entities/cryptoWithdraw/api'
import { Roles } from '@/entities/roles'
import { ThemeToggle } from '@/features/themeToggle/ui/ThemeToggle'
import { useLogout, useToast } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { RootState } from '@/store/store'
import { USER_ROLE_KEY } from '../auth'
import { BalanceCard, ExchangeInfo, NavMenu, UserActions } from './children'
import { ActionsDialog, TopupDialog, WithdrawDialog } from './dialogs'
import { BaseButton, MainIcon } from '@/shared/components'
import { X } from 'lucide-react'

type SideBarProps = {
  isSidebarOpen: boolean
  setIsSidebarOpen: (value: boolean) => void
}

export const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }: SideBarProps) => {
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const navigate = useNavigate()
  const logout = useLogout()
  const [modalActionsVisible, setModalActionsVisible] = useState(false)
  const [modalWithdrawVisible, setModalWithdrawVisible] = useState(false)
  const [modalTopupVisible, setModalTopupVisible] = useState(false)
  const { addToast } = useToast()
  const { balances } = useSelector((state: RootState) => state.balances)

  const balanceUser = balances.find((b) => b.balanceType === 1)?.amount ?? 0
  const { trader } = useSelector((state: RootState) => state.trader)
  const { merchant } = useSelector((state: RootState) => state.merchant)

  const onProfileClickHandler = () => {
    navigate('/profile')
    setIsSidebarOpen(false)
  }

  const { mutate: createWithdraw, isPending: isPandingCreateWithdraw } = useApiPost<
    CryptoWithdrawModel,
    NewCryptoWithdrawModel
  >(cryptoWithdrawsAPI.create, {
    onSuccess: () => {
      addToast('Успех!', 'Вывод создан', 'success')
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
    onSettled: () => {
      setModalWithdrawVisible(false)
    },
  })

  const handleCreateWithdraw = (data: NewCryptoWithdrawModel) => {
    createWithdraw(data)
  }

  const teamName =
    userRole === Roles.Trader
      ? (trader?.teamName ?? 'Нет команды')
      : userRole === Roles.Merchant
        ? (merchant?.name ?? 'Нет команды')
        : 'Нет команды'

  return (
    <div className="flex flex-col bg-white dark:bg-dark-bg md:w-72 h-[100dvh] md:h-[100vh]">
      <div
        className="flex flex-col p-4 lg:p-0 h-full overflow-y-auto scrollbar-hide"
        style={{ height: '100%' }}
      >
        <div className="lg:hidden flex justify-between items-center mt-2 pb-4 pl-4">
          <MainIcon />
          {isSidebarOpen && (
            <BaseButton
              variant="ghost"
              size="icon"
              className="lg:hidden z-60"
              onClick={() => setIsSidebarOpen(false)}
            >
              <X className="w-6 h-6 text-text-secondary" />
            </BaseButton>
          )}
        </div>

        <ExchangeInfo />

        <div className="mt-4 min-h-[210px] md:min-h-[285px] overflow-hidden">
          <NavMenu setIsSidebarOpen={setIsSidebarOpen} />
        </div>

        <div className="flex-shrink-0 mt-4">
          <BalanceCard
            onActions={() => setModalActionsVisible(true)}
            onTopup={() => setModalTopupVisible(true)}
            onWithdraw={() => setModalWithdrawVisible(true)}
          />

          <UserActions
            teamName={teamName}
            onProfileClick={onProfileClickHandler}
            onLogout={logout}
          />

          <div className="mt-3 pl-4 max-w-[220px] md:max-w-full">
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <TopupDialog
        isVisible={modalTopupVisible}
        onClose={() => setModalTopupVisible(false)}
        balanceRewards={balanceUser}
      />
      <ActionsDialog
        isVisible={modalActionsVisible}
        onClose={() => setModalActionsVisible(false)}
        onTopup={() => {
          setModalTopupVisible(true)
          setModalActionsVisible(false)
        }}
        onWithdraw={() => {
          setModalWithdrawVisible(true)
          setModalActionsVisible(false)
        }}
      />

      <WithdrawDialog
        isVisible={modalWithdrawVisible}
        onClose={() => setModalWithdrawVisible(false)}
        onSubmit={handleCreateWithdraw}
        isLoading={isPandingCreateWithdraw}
      />
    </div>
  )
}
